import { useState, useEffect } from 'react';
import { useChat } from '../context/ChatContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  X, 
  Download, 
  ChevronLeft,
  Users,
  DollarSign,
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  Calendar,
  MapPin,
  Loader2,
  Sparkles,
  AlertTriangle,
  CheckCircle,
  Target,
  Lightbulb,
  ArrowRight,
  Clock,
  BarChart3,
  PieChart as PieChartIcon,
  Activity,
  Zap,
  Shield,
  AlertCircle,
  ChevronRight,
  FileText,
  Send,
  Megaphone
} from 'lucide-react';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Legend
} from 'recharts';
import axios from 'axios';

import { API_BASE_URL as API } from '../utils/apiConfig';
const COLORS = ['#8B5CF6', '#EC4899', '#14B8A6', '#EAB308', '#3B82F6', '#F97316', '#10B981', '#6366F1'];

// Indian number formatting utility
const formatIndianNumber = (num) => {
  if (num === null || num === undefined) return '—';
  const absNum = Math.abs(num);
  if (absNum >= 10000000) {
    return `₹${(num / 10000000).toFixed(2)} Cr`;
  } else if (absNum >= 100000) {
    return `₹${(num / 100000).toFixed(2)} L`;
  }
  // Indian number system: 12,34,567
  const numStr = Math.round(absNum).toString();
  let result = '';
  const len = numStr.length;
  if (len <= 3) {
    result = numStr;
  } else {
    result = numStr.slice(-3);
    let remaining = numStr.slice(0, -3);
    while (remaining.length > 2) {
      result = remaining.slice(-2) + ',' + result;
      remaining = remaining.slice(0, -2);
    }
    if (remaining.length > 0) {
      result = remaining + ',' + result;
    }
  }
  return num < 0 ? '-' + result : result;
};

// Format value based on type
const formatValue = (value, format) => {
  if (value === null || value === undefined) return '—';
  
  switch (format) {
    case 'currency':
      const absVal = Math.abs(value);
      if (absVal >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
      if (absVal >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
      return `₹${formatIndianNumber(value)}`;
    case 'percent':
      return `${value}%`;
    case 'number':
    default:
      return formatIndianNumber(value);
  }
};

// Format axis values for charts (compact)
const formatAxisValue = (value) => {
  if (value === null || value === undefined) return '';
  const absVal = Math.abs(value);
  if (absVal >= 10000000) return `${(value / 10000000).toFixed(1)}Cr`;
  if (absVal >= 100000) return `${(value / 100000).toFixed(1)}L`;
  if (absVal >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toString();
};

// Format tooltip values
const formatTooltipValue = (value) => {
  if (value === null || value === undefined) return '—';
  const absVal = Math.abs(value);
  if (absVal >= 10000000) return `₹${(value / 10000000).toFixed(2)} Cr`;
  if (absVal >= 100000) return `₹${(value / 100000).toFixed(2)} L`;
  if (absVal >= 1000) return `₹${(value / 1000).toFixed(1)}K`;
  return formatIndianNumber(value);
};

// AI Insight Card Component
const AIInsightSection = ({ title, icon: Icon, children, severity = 'info' }) => {
  const severityColors = {
    success: 'from-emerald-500/10 to-teal-500/5 border-emerald-500/20',
    warning: 'from-amber-500/10 to-orange-500/5 border-amber-500/20',
    critical: 'from-red-500/10 to-pink-500/5 border-red-500/20',
    info: 'from-blue-500/10 to-cyan-500/5 border-blue-500/20',
    positive: 'from-emerald-500/10 to-teal-500/5 border-emerald-500/20',
    negative: 'from-red-500/10 to-pink-500/5 border-red-500/20',
    stable: 'from-blue-500/10 to-cyan-500/5 border-blue-500/20',
    needs_improvement: 'from-amber-500/10 to-orange-500/5 border-amber-500/20'
  };
  
  const iconColors = {
    success: 'text-emerald-400',
    warning: 'text-amber-400',
    critical: 'text-red-400',
    info: 'text-blue-400',
    positive: 'text-emerald-400',
    negative: 'text-red-400',
    stable: 'text-blue-400',
    needs_improvement: 'text-amber-400'
  };
  
  return (
    <Card className={`bg-gradient-to-br ${severityColors[severity] || severityColors.info}`}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg bg-background/50`}>
            <Icon className={`w-5 h-5 ${iconColors[severity] || iconColors.info}`} />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-sm mb-2">{title}</h4>
            {children}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Risk/Opportunity Badge
const RiskBadge = ({ level }) => {
  const config = {
    high: { color: 'bg-red-500/20 text-red-400 border-red-500/30', label: 'High Risk' },
    moderate: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', label: 'Moderate Risk' },
    low: { color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', label: 'Low Risk' }
  };
  const { color, label } = config[level] || config.moderate;
  return <Badge variant="outline" className={color}>{label}</Badge>;
};

// Action Item Component
const ActionItem = ({ action, priority = 'medium' }) => {
  const priorityColors = {
    high: 'border-l-red-500',
    medium: 'border-l-amber-500',
    low: 'border-l-blue-500'
  };
  
  return (
    <div className={`pl-3 border-l-2 ${priorityColors[priority]} py-1`}>
      <p className="text-sm">{action}</p>
    </div>
  );
};

export default function DrillDownModal({ 
  open, 
  onClose, 
  drillType, 
  drillData, 
  title,
  brandId 
}) {
  const { selectedBrand } = useChat();
  const [detailData, setDetailData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('insights'); // insights, data, chart
  const [pdfLoading, setPdfLoading] = useState(false);
  const [campaignLoading, setCampaignLoading] = useState(false);
  const [campaignCreated, setCampaignCreated] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('Loading data...');

  useEffect(() => {
    if (open && drillData) {
      fetchDetailData();
    }
  }, [open, drillData]);

  const fetchDetailData = async () => {
    const brand = brandId || selectedBrand?.brand_id;
    if (!brand) return;
    
    setLoading(true);
    setProgress(0);
    setProgressText('Fetching data...');
    
    // Smoother progress animation - reaches 95% before completion
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 95) return prev;
        // Faster initial progress, slower as it approaches 95%
        const increment = prev < 30 ? 8 : prev < 60 ? 5 : prev < 80 ? 3 : 1;
        return Math.min(prev + increment, 95);
      });
    }, 200);
    
    // Update progress text based on percentage
    const textInterval = setInterval(() => {
      setProgress(prev => {
        if (prev < 25) setProgressText('Fetching data...');
        else if (prev < 50) setProgressText('Running analysis...');
        else if (prev < 75) setProgressText('Building visualizations...');
        else if (prev < 90) setProgressText('Generating insights...');
        else setProgressText('Almost there...');
        return prev;
      });
    }, 500);
    
    try {
      const response = await axios.post(
        `${API}/api/drilldown/ai-insights`,
        {
          brand_id: brand,
          metric_type: drillType,
          metric_id: drillData?.value || drillType,
          metric_value: drillData?.value,
          context: drillData?.context || {},
          dashboard: drillData?.dashboard || 'executive'
        },
        { 
          withCredentials: true,
          timeout: 60000 // Increased timeout to 60 seconds
        }
      );
      clearInterval(progressInterval);
      clearInterval(textInterval);
      setProgress(100);
      setProgressText('Complete!');
      setDetailData(response.data.data);
    } catch (err) {
      console.error('Drill-down error:', err);
      // Try legacy endpoint as fallback
      try {
        const legacyResponse = await axios.post(
          `${API}/api/drilldown`,
          {
            brand_id: brand,
            drill_type: drillType,
            drill_value: drillData?.value,
            drill_context: drillData?.context || {}
          },
          { 
            withCredentials: true,
            timeout: 45000
          }
        );
        clearInterval(progressInterval);
        clearInterval(textInterval);
        setProgress(100);
        setDetailData(legacyResponse.data);
      } catch (legacyErr) {
        clearInterval(progressInterval);
        clearInterval(textInterval);
        console.error('Legacy drilldown also failed:', legacyErr);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleExport = () => {
    if (!detailData?.table) return;
    
    const headers = detailData.table.columns.map(c => c.label).join(',');
    const rows = detailData.table.rows.map(row => 
      detailData.table.columns.map(c => {
        const val = row[c.key];
        return typeof val === 'string' && val.includes(',') ? `"${val}"` : val;
      }).join(',')
    ).join('\n');
    
    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${drillType}_${drillData?.value || 'data'}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Download PDF Analyst Report
  const handleDownloadPDF = async () => {
    const brand = brandId || selectedBrand?.brand_id;
    if (!brand) return;
    
    setPdfLoading(true);
    try {
      const response = await axios.post(
        `${API}/api/drilldown/generate-pdf`,
        {
          brand_id: brand,
          report_type: drillData?.dashboard || 'drilldown',
          metric_type: drillType,
          metric_id: drillData?.value || drillType,
          context: drillData?.context || {},
          include_data_table: true
        },
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const brandName = selectedBrand?.name || 'Report';
      link.setAttribute('download', `FundleBrain_${brandName.replace(/\s+/g, '_')}_${drillType}_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download error:', err);
      alert('Failed to generate PDF report. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  // Schedule Instant Action Campaign
  const handleScheduleCampaign = async () => {
    const brand = brandId || selectedBrand?.brand_id;
    if (!brand) return;
    
    setCampaignLoading(true);
    setCampaignCreated(null);
    
    // Determine campaign type from drill type
    const insightTypeMap = {
      'churn': 'churn_risk',
      'at_risk': 'churn_risk',
      'high_value_risk': 'churn_risk',
      'tier': 'tier_upgrade',
      'one_timer': 'one_timer_conversion',
      'repeat': 'one_timer_conversion',
      'loyalty_contribution': 'loyalty_contribution',
      'active_members': 'loyalty_contribution',
      'kpi': 'general'
    };
    
    let insightType = 'general';
    const drillLower = (drillType || '').toLowerCase();
    const valueLower = (drillData?.value || '').toLowerCase();
    
    for (const [key, value] of Object.entries(insightTypeMap)) {
      if (drillLower.includes(key) || valueLower.includes(key)) {
        insightType = value;
        break;
      }
    }
    
    try {
      const response = await axios.post(
        `${API}/api/campaigns/instant-action`,
        {
          brand_id: brand,
          insight_type: insightType,
          context: {
            metric_type: drillType,
            metric_value: drillData?.value,
            ...drillData?.context
          }
        },
        { withCredentials: true }
      );
      
      setCampaignCreated(response.data.campaign);
    } catch (err) {
      console.error('Campaign creation error:', err);
      alert('Failed to create campaign. Please try again.');
    } finally {
      setCampaignLoading(false);
    }
  };

  if (!open) return null;

  const aiInsights = detailData?.ai_insights || {};
  const kpis = detailData?.kpis || [];
  const charts = detailData?.charts || [];
  const table = detailData?.table;
  const benchmarks = detailData?.benchmarks || {};
  const risks = detailData?.risks || [];
  const opportunities = detailData?.opportunities || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="drilldown-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-6xl max-h-[90vh] bg-card rounded-2xl border border-border shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border bg-gradient-to-r from-violet-500/5 via-transparent to-blue-500/5">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={onClose}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-amber-500" />
                {title || 'AI-Powered Analysis'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {drillType}: {drillData?.value}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* View Tabs */}
            <div className="flex items-center bg-muted rounded-lg p-1 mr-2">
              <Button 
                variant={activeTab === 'insights' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setActiveTab('insights')}
                className="gap-1"
              >
                <Sparkles className="w-3 h-3" />
                Insights
              </Button>
              <Button 
                variant={activeTab === 'data' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setActiveTab('data')}
                className="gap-1"
              >
                <BarChart3 className="w-3 h-3" />
                Data
              </Button>
              <Button 
                variant={activeTab === 'chart' ? 'default' : 'ghost'} 
                size="sm"
                onClick={() => setActiveTab('chart')}
                className="gap-1"
              >
                <Activity className="w-3 h-3" />
                Charts
              </Button>
            </div>
            {/* Action Buttons */}
            <Button 
              variant="default" 
              size="sm" 
              onClick={handleDownloadPDF}
              disabled={pdfLoading || loading}
              className="gap-2 bg-violet-600 hover:bg-violet-700"
              data-testid="download-pdf-btn"
            >
              {pdfLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              {pdfLoading ? 'Generating...' : 'Download PDF Report'}
            </Button>
            <Button 
              variant="default" 
              size="sm" 
              onClick={handleScheduleCampaign}
              disabled={campaignLoading || loading}
              className="gap-2 bg-emerald-600 hover:bg-emerald-700"
              data-testid="schedule-campaign-btn"
            >
              {campaignLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Megaphone className="w-4 h-4" />
              )}
              {campaignLoading ? 'Creating...' : 'Schedule Campaign'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleExport} className="gap-2">
              <Download className="w-4 h-4" />
              CSV
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Campaign Created Banner */}
        {campaignCreated && (
          <div className="mx-4 mb-2 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/20">
                  <CheckCircle className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <h4 className="font-semibold text-emerald-400">Campaign Draft Created!</h4>
                  <p className="text-sm text-muted-foreground mt-1">{campaignCreated.name}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Target: {formatIndianNumber(campaignCreated.segment?.estimated_size)} customers via {campaignCreated.channel}
                  </p>
                  <p className="text-xs text-emerald-400 mt-2">
                    Expected ROI: {campaignCreated.estimated_roi?.roi_multiplier || '4-6x'}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setCampaignCreated(null)}
                className="text-xs"
              >
                Dismiss
              </Button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="flex flex-col items-center gap-4 w-full max-w-md">
                <div className="relative">
                  <Sparkles className="w-12 h-12 text-amber-500 animate-pulse" />
                </div>
                <p className="text-muted-foreground">{progressText}</p>
                <div className="w-full max-w-xs">
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground text-center mt-2">{progress}%</p>
                </div>
              </div>
            </div>
          ) : detailData ? (
            <div className="space-y-6">
              {/* KPIs Row */}
              {kpis.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {kpis.map((kpi, idx) => (
                    <Card key={idx} className="relative overflow-hidden">
                      <CardContent className="p-4">
                        <p className="text-sm text-muted-foreground">{kpi.label}</p>
                        <p className="text-2xl font-bold">{formatValue(kpi.value, kpi.format)}</p>
                        {kpi.change && (
                          <p className={`text-sm flex items-center gap-1 ${kpi.trend === 'up' || kpi.change?.includes('+') ? 'text-emerald-500' : 'text-red-500'}`}>
                            {kpi.trend === 'up' || kpi.change?.includes('+') ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                            {kpi.change}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* AI Insights Tab */}
              {activeTab === 'insights' && aiInsights && (
                <div className="space-y-4">
                  {/* What is happening */}
                  {aiInsights.what && (
                    <AIInsightSection 
                      title={typeof aiInsights.what === 'string' ? "What's Happening" : (aiInsights.what.title || "What's Happening")}
                      icon={Activity}
                      severity={typeof aiInsights.what === 'object' ? aiInsights.what.direction : 'info'}
                    >
                      <p className="text-sm text-muted-foreground">
                        {typeof aiInsights.what === 'string' ? aiInsights.what : aiInsights.what.summary}
                      </p>
                      {typeof aiInsights.what === 'object' && aiInsights.what.magnitude && (
                        <Badge variant="outline" className="mt-2">{aiInsights.what.magnitude}</Badge>
                      )}
                      {typeof aiInsights.what === 'object' && aiInsights.what.anomaly && (
                        <div className="mt-2 flex items-center gap-2 text-amber-500 text-xs">
                          <AlertTriangle className="w-3 h-3" />
                          {aiInsights.what.anomaly}
                        </div>
                      )}
                    </AIInsightSection>
                  )}

                  {/* Why it's happening / Driver Analysis */}
                  {aiInsights.why && (
                    <AIInsightSection 
                      title={typeof aiInsights.why === 'string' ? "Why It's Happening" : (aiInsights.why.title || "Driver Analysis")}
                      icon={Lightbulb}
                      severity="info"
                    >
                      {typeof aiInsights.why === 'string' ? (
                        <p className="text-sm text-muted-foreground">{aiInsights.why}</p>
                      ) : (
                        <>
                          {/* Summary if available */}
                          {aiInsights.why.summary && (
                            <p className="text-sm text-muted-foreground mb-3">{aiInsights.why.summary}</p>
                          )}
                          
                          {/* Drivers array (new format) */}
                          {aiInsights.why.drivers && aiInsights.why.drivers.length > 0 && (
                            <ul className="space-y-3 text-sm">
                              {aiInsights.why.drivers.map((driver, idx) => (
                                <li key={idx} className="flex items-start gap-2 p-2 rounded bg-muted/30">
                                  <div className="flex-shrink-0 mt-0.5">
                                    {driver.trend === 'up' ? (
                                      <TrendingUp className="w-4 h-4 text-red-400" />
                                    ) : driver.trend === 'down' ? (
                                      <TrendingDown className="w-4 h-4 text-green-400" />
                                    ) : (
                                      <ChevronRight className="w-4 h-4 text-primary" />
                                    )}
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-foreground">{driver.factor}</span>
                                      {driver.contribution && (
                                        <Badge variant="outline" className="text-xs">{driver.contribution}</Badge>
                                      )}
                                    </div>
                                    {driver.detail && (
                                      <p className="text-xs text-muted-foreground mt-1">{driver.detail}</p>
                                    )}
                                  </div>
                                </li>
                              ))}
                            </ul>
                          )}
                          
                          {/* Legacy factors array (old format) */}
                          {aiInsights.why.factors && aiInsights.why.factors.length > 0 && !aiInsights.why.drivers && (
                            <ul className="space-y-2 text-sm text-muted-foreground">
                              {aiInsights.why.factors.map((factor, idx) => (
                                <li key={idx} className="flex items-start gap-2">
                                  <ChevronRight className="w-4 h-4 mt-0.5 text-primary" />
                                  {typeof factor === 'string' ? factor : factor.factor || JSON.stringify(factor)}
                                </li>
                              ))}
                            </ul>
                          )}
                          
                          {/* Cohort Analysis */}
                          {aiInsights.why.cohort_analysis && (
                            <div className="mt-3 p-2 rounded bg-muted/20">
                              <span className="font-medium text-xs">Cohort Analysis:</span>
                              <p className="text-xs text-muted-foreground mt-1">{aiInsights.why.cohort_analysis}</p>
                            </div>
                          )}
                          
                          {aiInsights.why.cohort_impact && (
                            <p className="mt-2 text-xs text-muted-foreground">
                              <span className="font-medium">Cohort Impact:</span> {aiInsights.why.cohort_impact}
                            </p>
                          )}
                          {aiInsights.why.geography_impact && (
                            <p className="text-xs text-muted-foreground">
                              <span className="font-medium">Geography:</span> {aiInsights.why.geography_impact}
                            </p>
                          )}
                        </>
                      )}
                    </AIInsightSection>
                  )}

                  {/* Business Impact */}
                  {aiInsights.impact && (
                    <AIInsightSection 
                      title={typeof aiInsights.impact === 'string' ? "Business Impact" : (aiInsights.impact.title || "Business Impact")}
                      icon={Target}
                      severity="warning"
                    >
                      {typeof aiInsights.impact === 'string' ? (
                        <p className="text-sm text-muted-foreground">{aiInsights.impact}</p>
                      ) : (
                        <div className="grid md:grid-cols-2 gap-3 text-sm">
                          {aiInsights.impact.revenue_impact && (
                            <div className="flex items-center gap-2">
                              <DollarSign className="w-4 h-4 text-emerald-500" />
                              <span className="text-muted-foreground">{aiInsights.impact.revenue_impact}</span>
                            </div>
                          )}
                          {aiInsights.impact.margin_impact && (
                            <div className="flex items-center gap-2">
                              <TrendingUp className="w-4 h-4 text-blue-500" />
                              <span className="text-muted-foreground">{aiInsights.impact.margin_impact}</span>
                            </div>
                          )}
                          {aiInsights.impact.clv_impact && (
                            <div className="flex items-center gap-2">
                              <Users className="w-4 h-4 text-violet-500" />
                              <span className="text-muted-foreground">{aiInsights.impact.clv_impact}</span>
                            </div>
                          )}
                          {aiInsights.impact.churn_impact && (
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="w-4 h-4 text-red-500" />
                              <span className="text-muted-foreground">{aiInsights.impact.churn_impact}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </AIInsightSection>
                  )}

                  {/* Recommended Actions */}
                  {(aiInsights.action || aiInsights.actions) && (
                    <AIInsightSection 
                      title={aiInsights.actions?.title || (typeof aiInsights.action === 'string' ? "Recommended Actions" : (aiInsights.action?.title || "Recommended Actions"))}
                      icon={Zap}
                      severity="success"
                    >
                      {/* New structured format with p0/p1/p2 */}
                      {aiInsights.actions && (
                        <div className="space-y-4">
                          {aiInsights.actions.summary && (
                            <p className="text-sm font-medium text-foreground mb-3">{aiInsights.actions.summary}</p>
                          )}
                          {aiInsights.actions.p0 && (
                            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                              <p className="text-xs font-bold text-red-400 mb-1">P0 - IMMEDIATE</p>
                              <p className="text-sm font-medium">{aiInsights.actions.p0.action}</p>
                              <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
                                <span className="bg-background/50 px-2 py-0.5 rounded">Owner: {aiInsights.actions.p0.owner}</span>
                                <span className="bg-background/50 px-2 py-0.5 rounded">Impact: {aiInsights.actions.p0.impact}</span>
                                <span className="bg-background/50 px-2 py-0.5 rounded">Track: {aiInsights.actions.p0.kpi}</span>
                              </div>
                            </div>
                          )}
                          {aiInsights.actions.p1 && (
                            <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                              <p className="text-xs font-bold text-amber-400 mb-1">P1 - THIS MONTH</p>
                              <p className="text-sm font-medium">{aiInsights.actions.p1.action}</p>
                              <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
                                <span className="bg-background/50 px-2 py-0.5 rounded">Owner: {aiInsights.actions.p1.owner}</span>
                                <span className="bg-background/50 px-2 py-0.5 rounded">Impact: {aiInsights.actions.p1.impact}</span>
                                <span className="bg-background/50 px-2 py-0.5 rounded">Track: {aiInsights.actions.p1.kpi}</span>
                              </div>
                            </div>
                          )}
                          {aiInsights.actions.p2 && (
                            <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                              <p className="text-xs font-bold text-blue-400 mb-1">P2 - THIS QUARTER</p>
                              <p className="text-sm font-medium">{aiInsights.actions.p2.action}</p>
                              <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
                                <span className="bg-background/50 px-2 py-0.5 rounded">Owner: {aiInsights.actions.p2.owner}</span>
                                <span className="bg-background/50 px-2 py-0.5 rounded">Impact: {aiInsights.actions.p2.impact}</span>
                                <span className="bg-background/50 px-2 py-0.5 rounded">Track: {aiInsights.actions.p2.kpi}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {/* Old string format fallback */}
                      {!aiInsights.actions && typeof aiInsights.action === 'string' && (
                        <p className="text-sm text-muted-foreground">{aiInsights.action}</p>
                      )}
                      {/* Old immediate/short_term/long_term format */}
                      {!aiInsights.actions && typeof aiInsights.action === 'object' && aiInsights.action.immediate && (
                        <div className="space-y-4">
                          {aiInsights.action.immediate.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-red-400 mb-2">IMMEDIATE (This Week)</p>
                            <div className="space-y-2">
                              {aiInsights.action.immediate.map((action, idx) => (
                                <ActionItem key={idx} action={action} priority="high" />
                              ))}
                            </div>
                          </div>
                        )}
                        {aiInsights.action.short_term && aiInsights.action.short_term.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-amber-400 mb-2">SHORT TERM (This Month)</p>
                            <div className="space-y-2">
                              {aiInsights.action.short_term.map((action, idx) => (
                                <ActionItem key={idx} action={action} priority="medium" />
                              ))}
                            </div>
                          </div>
                        )}
                        {aiInsights.action.long_term && aiInsights.action.long_term.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-blue-400 mb-2">LONG TERM (This Quarter)</p>
                            <div className="space-y-2">
                              {aiInsights.action.long_term.map((action, idx) => (
                                <ActionItem key={idx} action={action} priority="low" />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      )}
                    </AIInsightSection>
                  )}

                  {/* Campaign Suggestion */}
                  {aiInsights.campaign_suggestion && (
                    <Card className="bg-gradient-to-br from-violet-500/10 to-purple-500/5 border-violet-500/20">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-background/50">
                            <Target className="w-5 h-5 text-violet-400" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-sm mb-2">Suggested Campaign</h4>
                            <div className="space-y-2 text-sm">
                              <p><span className="text-muted-foreground">Segment:</span> <span className="font-medium">{aiInsights.campaign_suggestion.segment}</span></p>
                              <p><span className="text-muted-foreground">Channel:</span> <span className="font-medium">{aiInsights.campaign_suggestion.channel}</span></p>
                              <p><span className="text-muted-foreground">Message Theme:</span> <span className="font-medium">{aiInsights.campaign_suggestion.message_theme}</span></p>
                              <div className="flex gap-4 mt-2">
                                <span className="text-emerald-400">Expected ROI: {aiInsights.campaign_suggestion.expected_roi}</span>
                                <Badge variant="outline" className={aiInsights.campaign_suggestion.urgency === 'high' ? 'bg-red-500/20 text-red-400' : aiInsights.campaign_suggestion.urgency === 'medium' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}>
                                  {aiInsights.campaign_suggestion.urgency} urgency
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Risks & Opportunities */}
                  {(risks.length > 0 || opportunities.length > 0) && (
                    <div className="grid md:grid-cols-2 gap-4">
                      {risks.length > 0 && (
                        <Card className="bg-gradient-to-br from-red-500/10 to-pink-500/5 border-red-500/20">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <AlertCircle className="w-4 h-4 text-red-400" />
                              Risks
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {risks.map((risk, idx) => (
                              <div key={idx} className="text-sm">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium">{typeof risk === 'string' ? risk : risk.risk}</span>
                                  {typeof risk === 'object' && risk.severity && <RiskBadge level={risk.severity} />}
                                </div>
                                {typeof risk === 'object' && risk.description && (
                                  <p className="text-xs text-muted-foreground">{risk.description}</p>
                                )}
                                {typeof risk === 'object' && risk.mitigation && (
                                  <p className="text-xs text-primary mt-1">💡 {risk.mitigation}</p>
                                )}
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      )}
                      
                      {opportunities.length > 0 && (
                        <Card className="bg-gradient-to-br from-emerald-500/10 to-teal-500/5 border-emerald-500/20">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <CheckCircle className="w-4 h-4 text-emerald-400" />
                              Opportunities
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {opportunities.map((opp, idx) => (
                              <div key={idx} className="text-sm">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium">{typeof opp === 'string' ? opp : opp.opportunity}</span>
                                  {typeof opp === 'object' && opp.potential && (
                                    <Badge variant="outline" className="bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                                      {opp.potential}
                                    </Badge>
                                  )}
                                </div>
                                {typeof opp === 'object' && opp.description && (
                                  <p className="text-xs text-muted-foreground">{opp.description}</p>
                                )}
                                {typeof opp === 'object' && opp.action && (
                                  <p className="text-xs text-primary mt-1">→ {opp.action}</p>
                                )}
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}

                  {/* Benchmarks */}
                  {Object.keys(benchmarks).length > 0 && (
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <Shield className="w-4 h-4 text-blue-500" />
                          Benchmarks
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          {Object.entries(benchmarks).map(([key, value], idx) => (
                            <div key={idx}>
                              <p className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</p>
                              <p className="font-medium">
                                {typeof value === 'object' ? (
                                  <span>
                                    {value.current ?? value.value ?? '-'}
                                    {value.variance_pct !== undefined && (
                                      <span className={`ml-1 text-xs ${value.variance_pct >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                                        ({value.variance_pct >= 0 ? '+' : ''}{value.variance_pct}%)
                                      </span>
                                    )}
                                  </span>
                                ) : value}
                              </p>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>
              )}

              {/* Data Tab */}
              {activeTab === 'data' && table && table.rows && table.rows.length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                      {table.title || 'Detailed Data'} ({table.rows.length} records)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
                      <table className="w-full">
                        <thead className="sticky top-0 bg-card">
                          <tr className="border-b border-border bg-muted/30">
                            {table.columns.map((col) => (
                              <th key={col.key} className="px-4 py-3 text-left text-sm font-semibold">
                                {col.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {table.rows.map((row, idx) => (
                            <tr key={idx} className="border-b border-border/50 hover:bg-muted/20">
                              {table.columns.map((col) => (
                                <td key={col.key} className="px-4 py-3 text-sm">
                                  {formatValue(row[col.key], col.type)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Charts Tab */}
              {activeTab === 'chart' && charts.length > 0 && (
                <div className="grid lg:grid-cols-2 gap-6">
                  {charts.map((chart, idx) => (
                    <Card key={idx}>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base">{chart.title}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          {chart.type === 'bar' ? (
                            <BarChart data={chart.data}>
                              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                              <XAxis dataKey={chart.xKey} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => formatAxisValue(v)} />
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: 'hsl(var(--card))', 
                                  border: '1px solid hsl(var(--border))',
                                  borderRadius: '8px'
                                }}
                                formatter={(value) => formatTooltipValue(value)}
                              />
                              <Bar dataKey={chart.yKey} fill="#8B5CF6" radius={[4, 4, 0, 0]}>
                                {chart.data?.map((entry, i) => (
                                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                ))}
                              </Bar>
                            </BarChart>
                          ) : chart.type === 'line' ? (
                            <LineChart data={chart.data}>
                              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                              <XAxis dataKey={chart.xKey} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => formatAxisValue(v)} />
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: 'hsl(var(--card))', 
                                  border: '1px solid hsl(var(--border))',
                                  borderRadius: '8px'
                                }}
                                formatter={(value) => formatTooltipValue(value)}
                              />
                              <Line type="monotone" dataKey={chart.yKey} stroke="#8B5CF6" strokeWidth={2} dot={{ fill: '#8B5CF6' }} />
                            </LineChart>
                          ) : chart.type === 'area' ? (
                            <AreaChart data={chart.data}>
                              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                              <XAxis dataKey={chart.xKey} tick={{ fontSize: 11 }} />
                              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => formatAxisValue(v)} />
                              <Tooltip 
                                contentStyle={{ 
                                  backgroundColor: 'hsl(var(--card))', 
                                  border: '1px solid hsl(var(--border))',
                                  borderRadius: '8px'
                                }}
                              />
                              <Area type="monotone" dataKey={chart.yKey} stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.3} />
                            </AreaChart>
                          ) : (
                            <PieChart>
                              <Pie
                                data={chart.data}
                                dataKey={chart.yKey}
                                nameKey={chart.xKey}
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={100}
                                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                labelLine={false}
                              >
                                {chart.data?.map((entry, i) => (
                                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip />
                            </PieChart>
                          )}
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Legacy insights support */}
              {detailData.insights && typeof detailData.insights === 'string' && !aiInsights.what && (
                <Card className="bg-primary/5 border-primary/20">
                  <CardContent className="p-4">
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-amber-500" />
                      AI Insights
                    </h3>
                    <p className="text-sm text-muted-foreground">{detailData.insights}</p>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              No data available
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
